import { useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../lib/api";
import { Button } from "./ui/button";

let razorpayScriptPromise;

function loadRazorpayScript() {
  if (window.Razorpay) {
    return Promise.resolve();
  }
  if (razorpayScriptPromise) {
    return razorpayScriptPromise;
  }
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error("Razorpay checkout could not load"));
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

export default function RazorpayCheckoutButton({
  amount,
  billId = "",
  onPaymentComplete,
  publicToken = "",
  societyName = "Society",
  testId = "razorpay-checkout-button",
}) {
  const [paying, setPaying] = useState(false);

  const endpoint = publicToken
    ? `${API}/public/fresh-finance/payments/${publicToken}`
    : "/resident/finance/payments";

  const request = (path, body) => {
    if (publicToken) {
      if (path === "/orders") {
        return api.post(`${endpoint}${path}`);
      }
      return api.post(`${endpoint}${path}`, body);
    }
    return api.post(`${endpoint}${path}`, body);
  };

  const pay = async () => {
    if (!Number(amount) || Number(amount) < 1) {
      toast.error("Enter a payment amount of at least ₹1");
      return;
    }
    setPaying(true);
    try {
      const [orderResponse] = await Promise.all([
        request("/orders", { bill_id: billId, amount: Number(amount) }),
        loadRazorpayScript(),
      ]);
      const order = orderResponse.data;
      const checkout = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: societyName,
        description: billId ? "Maintenance bill payment" : "Maintenance payment",
        order_id: order.order_id,
        handler: async (response) => {
          try {
            const verification = await request("/verify", response);
            if (verification.data.status === "PAID") {
              toast.success("Payment confirmed and receipt posted");
              onPaymentComplete?.(verification.data);
              return;
            }
            toast.message("Payment verified. Your receipt will appear once Razorpay captures it.");
            onPaymentComplete?.(verification.data);
          } catch (error) {
            toast.error(error.response?.data?.detail || "Payment confirmation is pending");
          }
        },
        modal: {
          confirm_close: true,
          ondismiss: () => toast.message("Payment was cancelled"),
        },
        theme: { color: "#0284C7" },
      });
      checkout.on("payment.failed", (event) => {
        toast.error(event.error?.description || "Payment failed. Please retry.");
      });
      checkout.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Could not start payment");
    } finally {
      setPaying(false);
    }
  };

  return (
    <Button
      data-testid={testId}
      disabled={paying}
      onClick={pay}
      type="button"
    >
      {paying ? <Loader2 className="animate-spin" /> : <CreditCard />}
      {paying ? "Opening checkout…" : `Pay ₹${Number(amount || 0).toFixed(2)}`}
    </Button>
  );
}